import React, { useState, useEffect, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Textarea } from "../../Components/UI/textarea";
import { Label } from "../../Components/UI/label";
import { Image as ImageIcon, Trash2, Check, X, ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";
import { POST_CATEGORIES } from "./PostCategoriesConfig";
import { getAuthData } from "../../Utils/AuthUtils";
import { getCurrentUser } from "../../Utils/Utilities";
import { setPostAPI } from "./FeedAPI";
import apiService from "../../Services/APIService";
import { COMMUNITY_ALLOW_MULTIPLE_IMAGES } from "../../Utils/FeatureFlags";

export default function FeedEntry({ open, onClose, onPostCreated }) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState(POST_CATEGORIES[0].id);
  const [publicComments, setPublicComments] = useState(true);
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const authData = getAuthData();
  const currentUser = getCurrentUser() || {};
  const companyName = authData?.AgencyName || currentUser?.AgencyName || "Our Business";

  useEffect(() => {
    if (!open) {
      setTitle("");
      setDetails("");
      setFiles([]);
      setPreviewUrls([]);
      setLoading(false);
      setCategory(POST_CATEGORIES[0].id);
      setPublicComments(true);
      setDraggedIndex(null);
      setDragOverIndex(null);
    }
  }, [open]);

  useEffect(() => {
    if (!files.length) {
      setPreviewUrls([]);
      return;
    }
    const objectUrls = files.map((file) => URL.createObjectURL(file));
    setPreviewUrls(objectUrls);
    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
  const allowMulti = COMMUNITY_ALLOW_MULTIPLE_IMAGES;
  const MAX_PHOTOS_COUNT = allowMulti ? 5 : 1;

  const handleFileChange = (e) => {
    let selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    if (!allowMulti) {
      selectedFiles = selectedFiles.slice(0, 1);
    } else {
      const remainingSlots = MAX_PHOTOS_COUNT - files.length;
      if (remainingSlots <= 0) {
        toast.error(`Maximum ${MAX_PHOTOS_COUNT} images allowed.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (selectedFiles.length > remainingSlots) {
        // Pick the latest selected files (from the end of the selection)
        selectedFiles = selectedFiles.slice(-remainingSlots);
      }
    }

    const validFiles = [];
    for (const file of selectedFiles) {
      if (!file.type.startsWith("image/")) {
        toast.error(`"${file.name}" is not a valid image file.`);
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        toast.error(`"${file.name}" is too large.`, { description: "Please select images up to 2MB each." });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setFiles((prev) => (allowMulti ? [...prev, ...validFiles] : validFiles.slice(0, 1)));
    }
    // Reset file input value so selecting the same file again triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const touchStartIndexRef = useRef(null);
  const currentTouchTargetIndexRef = useRef(null);

  const reorderFiles = (fromIndex, toIndex) => {
    if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;
    setFiles((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  // HTML5 Drag and Drop handlers (Desktop)
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e, index) => {
    if (dragOverIndex === index) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    reorderFiles(draggedIndex, targetIndex);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch Drag handlers (Mobile)
  const handleTouchStart = (index) => {
    touchStartIndexRef.current = index;
    currentTouchTargetIndexRef.current = index;
    setDraggedIndex(index);
  };

  const handleTouchMove = (e) => {
    if (touchStartIndexRef.current === null) return;
    const touch = e.touches[0];
    const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);
    const thumbItem = targetElement?.closest("[data-thumb-index]");
    if (thumbItem) {
      const targetIndex = Number(thumbItem.getAttribute("data-thumb-index"));
      if (!isNaN(targetIndex) && targetIndex !== currentTouchTargetIndexRef.current) {
        currentTouchTargetIndexRef.current = targetIndex;
        setDragOverIndex(targetIndex);
      }
    }
  };

  const handleTouchEnd = () => {
    if (
      touchStartIndexRef.current !== null &&
      currentTouchTargetIndexRef.current !== null &&
      touchStartIndexRef.current !== currentTouchTargetIndexRef.current
    ) {
      reorderFiles(touchStartIndexRef.current, currentTouchTargetIndexRef.current);
    }
    touchStartIndexRef.current = null;
    currentTouchTargetIndexRef.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const dynamicPlaceholders = useMemo(() => {
    const selected = POST_CATEGORIES.find((c) => c.id === category) || POST_CATEGORIES[0];
    return {
      title: selected.titleTpl.replace(/{company}/g, companyName),
      details: selected.detailsTpl.replace(/{company}/g, companyName)
    };
  }, [category, companyName]);

  const handleDialogOpenChange = (isOpen) => {
    if (!isOpen) onClose();
  };

  const handleSubmitPost = async () => {
    if (!title.trim()) return toast.error("Please enter a title for your post.");
    if (title.length < 5) return toast.error("Title must be at least 5 characters long.");
    if (!details.trim()) return toast.error("Please enter details for your post.");

    setLoading(true);
    try {
      const uploadedPaths = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const uploadResult = await apiService.uploadFile(f, "Feed");
        if (uploadResult?.error) {
          throw new Error(uploadResult.message || `Failed to upload image ${i + 1}.`);
        }
        const path = uploadResult?.data?.relativePath || "";
        if (path) {
          uploadedPaths.push(path);
        }
      }

      await setPostAPI({
        postID: 0,
        title: title.trim(),
        detail: details.trim(),
        postTypeID: category,
        audienceTypeID: publicComments ? '1' : '2',
        photos: uploadedPaths,
        isDeleted: 0
      });

      toast.success("Post created.");
      if (onPostCreated) onPostCreated();
      onClose();
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error("This post could not be saved.", {
        description: error?.message || "Please try again or contact support if the issue persists.",
        duration: 4000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-w-[100vw] sm:max-w-[560px]"
      >
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">Create Post</div>
              </DialogTitle>
            </div>
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Create Community Post
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose className="flex-shrink-0" tabIndex={-1}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="px-4 flex-1 overflow-y-auto max-h-[calc(100vh-100px)] sm:max-h-[75vh]">
          <div className="space-y-4">
            {/* Post Category Selection */}
            <div className="space-y-1.5">
              <Label>What are you sharing?</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {POST_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const isSelected = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      className={`flex items-center justify-center gap-2 p-2.5 sahi-radius border text-center text-xs font-semibold transition-all cursor-pointer bg-sahi-white ${isSelected
                        ? "border-sahi-green text-sahi-green shadow-xs"
                        : " text-sub-text-color border-sahi-border-color hover:border-gray-400 hover:bg-gray-50"
                        }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${isSelected ? "text-sahi-green" : "text-sub-text-color"}`} />
                      <span className="truncate">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title Field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="postTitle" required>
                  Title
                </Label>
                {title.length >= 145 && (
                  <span className="text-[11px] text-sub-text-color">{title.length}/150</span>
                )}
              </div>
              <Input
                id="postTitle"
                name="postTitle"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 150))}
                placeholder={dynamicPlaceholders.title}
                className="h-11 text-sm sahi-input"
                maxLength={150}
              />
            </div>

            {/* Details Field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="postDetails" required>
                  Details
                </Label>
                {details.length >= 495 && (
                  <span className="text-[11px] text-sub-text-color">{details.length}/500</span>
                )}
              </div>
              <Textarea
                id="postDetails"
                name="postDetails"
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, 500))}
                placeholder={dynamicPlaceholders.details}
                rows={5}
                className="min-h-24 text-sm sahi-input"
                maxLength={500}
              />
            </div>

            {/* Images Upload Area */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{allowMulti ? "Attach Images" : "Attach Image"}</Label>
                {allowMulti && (
                  <span className="text-[11px] font-medium text-sub-text-color">
                    {files.length > 1 ? "Drag to reorder • " : ""}
                    {files.length}/{MAX_PHOTOS_COUNT}
                  </span>
                )}
              </div>

              {previewUrls.length > 0 ? (
                <div className="space-y-2">
                  <div className={allowMulti ? "grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-2.5" : "flex gap-3"}>
                    {previewUrls.map((url, idx) => (
                      <div
                        key={idx}
                        data-thumb-index={idx}
                        draggable={allowMulti}
                        onDragStart={allowMulti ? (e) => handleDragStart(e, idx) : undefined}
                        onDragOver={allowMulti ? (e) => handleDragOver(e, idx) : undefined}
                        onDragLeave={allowMulti ? (e) => handleDragLeave(e, idx) : undefined}
                        onDrop={allowMulti ? (e) => handleDrop(e, idx) : undefined}
                        onDragEnd={allowMulti ? handleDragEnd : undefined}
                        onTouchStart={allowMulti ? () => handleTouchStart(idx) : undefined}
                        onTouchMove={allowMulti ? handleTouchMove : undefined}
                        onTouchEnd={allowMulti ? handleTouchEnd : undefined}
                        className={`relative group rounded-xl border overflow-hidden aspect-square flex items-center justify-center shadow-2xs transition-all select-none ${allowMulti ? "w-full cursor-grab active:cursor-grabbing" : "w-28 sm:w-32"
                          } ${allowMulti && draggedIndex === idx
                            ? "opacity-35 scale-95 border-dashed border-sahi-primary-color bg-gray-100"
                            : allowMulti && dragOverIndex === idx
                              ? "ring-2 ring-sahi-primary-color ring-offset-2 scale-105 border-sahi-primary-color bg-sahi-disable"
                              : "border-sahi-border-color bg-sahi-disable hover:shadow-sm"
                          }`}
                        title={allowMulti ? "Drag to reorder" : undefined}
                      >
                        <img
                          src={url}
                          alt={allowMulti ? `Preview ${idx + 1}` : "Preview"}
                          className="w-full h-full object-cover pointer-events-none select-none"
                          draggable={false}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(idx);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 p-1 bg-sahi-red/90 text-white rounded-full hover:bg-sahi-red shadow-sm transition-colors cursor-pointer z-10"
                          title="Remove image"
                        >
                          <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </button>
                        {allowMulti && (
                          <div className="absolute bottom-1 left-1 sm:bottom-1.5 sm:left-1.5 px-1.5 py-0.5 rounded bg-black/65 backdrop-blur-xs text-[10px] text-white font-semibold pointer-events-none z-10">
                            {idx + 1}
                          </div>
                        )}
                      </div>
                    ))}

                    {allowMulti && files.length < MAX_PHOTOS_COUNT && (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="relative group rounded-xl bg-zinc-50 hover:bg-zinc-100 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors w-full aspect-square"
                      >
                        <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-xl" xmlns="http://www.w3.org/2000/svg">
                          <rect
                            x="1"
                            y="1"
                            width="calc(100% - 2px)"
                            height="calc(100% - 2px)"
                            rx="10"
                            ry="10"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeDasharray="4 3"
                            className="text-zinc-300 group-hover:text-zinc-400 transition-colors"
                          />
                        </svg>
                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white flex items-center justify-center shadow-2xs group-hover:scale-105 transition-transform z-1">
                          <Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-zinc-500" />
                        </div>
                        <span className="text-[11px] sm:text-xs font-semibold text-text-color z-1">Add More</span>
                      </div>
                    )}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple={allowMulti}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="relative w-full p-5 py-8 rounded-xl bg-white hover:bg-zinc-50 flex flex-col items-center justify-center gap-1 cursor-pointer transition-all shadow-xs group text-center"
                >
                  <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-xl" xmlns="http://www.w3.org/2000/svg">
                    <rect
                      x="1.5"
                      y="1.5"
                      width="calc(100% - 3px)"
                      height="calc(100% - 3px)"
                      rx="10"
                      ry="10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray="5 4"
                      className="text-zinc-300 group-hover:text-zinc-400 transition-colors"
                    />
                  </svg>
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform z-1 mb-1">
                    <Upload className="h-4 w-4 text-zinc-500" />
                  </div>
                  <p className="text-xs font-semibold text-text-color z-1">
                    {allowMulti ? "Click to upload images (up to 5)" : "Click to upload image"}
                  </p>
                  <p className="text-[11px] text-sub-text-color mt-0.5 z-1">
                    {allowMulti ? "PNG, JPG & JPEG up to 2MB each" : "PNG, JPG & JPEG up to 2MB"}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple={allowMulti}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            {/* Comments Visibility Checkbox */}
            <div className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
              <input
                type="checkbox"
                id="allowComments"
                checked={publicComments}
                onChange={(e) => setPublicComments(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
              />
              <Label htmlFor="allowComments" className="cursor-pointer mb-0">
                Allow others to see comments
              </Label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="py-4 space-y-4 mt-2">
            <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-start">
              <Button
                onClick={handleSubmitPost}
                disabled={loading}
                className="sahi-primary-button w-full sm:w-auto"
              >
                <Check className="h-4 w-4" />
                {loading ? "Sharing..." : "Share Post"}
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="sahi-secondary-button w-full sm:w-auto hidden sm:flex"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
